<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FilterPill from 'dashboard/components-next/filter-bar/FilterPill.vue';
import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'dashboard/components-next/dropdown-menu/base';

// UI-FILTROS-01 (§2.2): a barra de filtros padrão, tirada da do Kanban (CRM-01h). Busca e uma
// pílula por campo, no formato do HubSpot; o que se usa menos (`extra`) mora em "Mais filtros"
// até ganhar valor. No celular, as pílulas ficam atrás do botão "Filtros · N"; a busca, o
// período e os interruptores ficam à vista.
const props = defineProps({
  modelValue: { type: Object, required: true },
  // [{ key, label, options, single?, scalar?, extra?, disabled?, disabledHint? }]
  fields: { type: Array, default: () => [] },
  // { key = 'q', placeholder }, ou false para uma barra sem busca.
  search: { type: [Object, Boolean], default: () => ({}) },
  debounce: { type: Number, default: 400 },
  // Chaves de extra que a tela quer à vista mesmo sem valor, como as de "Mais filtros".
  revealed: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'reset']);

const { t } = useI18n();

const update = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

// A pílula trabalha com lista; o campo `scalar` guarda um valor só (ex.: dias na etapa).
const valorDe = campo => {
  const valor = props.modelValue[campo.key];
  if (!campo.scalar) return valor || [];
  return valor === null || valor === undefined || valor === '' ? [] : [valor];
};
const mudar = (campo, valores) =>
  update({ [campo.key]: campo.scalar ? (valores[0] ?? null) : valores });

const temValor = campo => valorDe(campo).length > 0;

// Extra aparece quando é pedido em "Mais filtros" ou pela tela (`revealed`), ou quando já tem
// valor (veio de um link).
const revelados = ref([]);
const extrasVisiveis = computed(() =>
  props.fields.filter(
    campo =>
      campo.extra &&
      (revelados.value.includes(campo.key) ||
        props.revealed.includes(campo.key) ||
        temValor(campo))
  )
);
const extrasEscondidos = computed(() =>
  props.fields.filter(
    campo => campo.extra && !extrasVisiveis.value.includes(campo)
  )
);
const pilulas = computed(() => [
  ...props.fields.filter(campo => !campo.extra),
  ...extrasVisiveis.value,
]);

// N conta só as pílulas que filtram: a busca, o período e os interruptores ficam de fora, e a
// pílula apagada também, porque o valor dela não vale naquela tela.
const quantosAtivos = computed(
  () => props.fields.filter(campo => !campo.disabled && temValor(campo)).length
);

const chaveDaBusca = computed(() =>
  props.search === false ? null : props.search.key || 'q'
);
const textoNoModelo = () =>
  chaveDaBusca.value ? props.modelValue[chaveDaBusca.value] || '' : '';

const temFiltro = computed(
  () => quantosAtivos.value > 0 || Boolean(textoNoModelo())
);

// Busca: espera parar de digitar para não pedir a lista a cada tecla, e acompanha o valor de
// fora ("Limpar" zera a busca também, e o campo tem de mostrar isso).
const busca = ref(textoNoModelo());
let espera = null;
watch(busca, valor => {
  clearTimeout(espera);
  if (valor === textoNoModelo()) return;
  espera = setTimeout(
    () => update({ [chaveDaBusca.value]: valor }),
    props.debounce
  );
});
watch(textoNoModelo, valor => {
  if (valor !== busca.value) busca.value = valor;
});
onBeforeUnmount(() => clearTimeout(espera));

const textoDaBusca = computed(
  () => props.search?.placeholder || t('FILTER_BAR.SEARCH_OPTIONS')
);

const abertoNoCelular = ref(false);
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <div
      v-if="chaveDaBusca || fields.length"
      class="flex items-center w-full gap-2 md:w-auto"
    >
      <label
        v-if="chaveDaBusca"
        class="flex items-center flex-1 h-8 gap-1.5 px-2.5 rounded-lg md:flex-none md:w-64 outline outline-1 outline-n-weak focus-within:outline-n-brand"
      >
        <Icon
          icon="i-lucide-search"
          class="flex-shrink-0 size-3.5 text-n-slate-11"
        />
        <input
          v-model="busca"
          type="search"
          :placeholder="textoDaBusca"
          :aria-label="textoDaBusca"
          class="w-full h-8 min-w-0 text-sm bg-transparent reset-base text-n-slate-12 !outline-0"
        />
      </label>
      <button
        v-if="fields.length"
        type="button"
        class="flex items-center flex-shrink-0 h-8 gap-1.5 px-2.5 text-sm rounded-lg md:hidden"
        :class="
          quantosAtivos
            ? 'bg-pz-brand-tint text-pz-brand-on-tint font-medium'
            : 'outline outline-1 outline-n-weak text-n-slate-12'
        "
        :aria-expanded="abertoNoCelular"
        @click="abertoNoCelular = !abertoNoCelular"
      >
        <Icon icon="i-lucide-list-filter" class="size-3.5" />
        {{
          quantosAtivos
            ? t('FILTER_BAR.BUTTON_COUNT', { count: quantosAtivos })
            : t('FILTER_BAR.BUTTON')
        }}
      </button>
    </div>

    <slot name="period" />
    <slot name="toggles" />

    <div
      v-if="fields.length"
      class="flex-wrap items-center gap-2"
      :class="abertoNoCelular ? 'flex' : 'hidden md:flex'"
    >
      <template v-for="campo in pilulas" :key="campo.key">
        <slot
          :name="`pill-${campo.key}`"
          :field="campo"
          :value="valorDe(campo)"
          :update="valores => mudar(campo, valores)"
        >
          <FilterPill
            :label="campo.label"
            :options="campo.options || []"
            :single="campo.single"
            :disabled="campo.disabled"
            :disabled-hint="campo.disabledHint"
            :all-label="campo.allLabel"
            :clearable="campo.clearable"
            :select-all="campo.selectAll"
            :search-from="campo.searchFrom"
            :model-value="valorDe(campo)"
            @update:model-value="valores => mudar(campo, valores)"
          />
        </slot>
      </template>

      <DropdownContainer v-if="extrasEscondidos.length">
        <template #trigger="{ toggle, isOpen }">
          <button
            type="button"
            class="flex items-center h-8 gap-1.5 px-2.5 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            :aria-expanded="isOpen"
            @click="toggle"
          >
            <Icon icon="i-lucide-list-filter" class="size-3.5" />
            {{ t('FILTER_BAR.MORE') }}
          </button>
        </template>
        <!-- No celular, fixa na largura da tela, como a lista da pílula: ancorada no botão perto
             da borda direita, sairia da tela. -->
        <DropdownBody
          class="z-50 mt-1 min-w-48 max-sm:fixed max-sm:inset-x-4 max-sm:min-w-0"
        >
          <DropdownSection>
            <DropdownItem
              v-for="extra in extrasEscondidos"
              :key="extra.key"
              type="button"
              class="cursor-pointer"
              :label="extra.label"
              :click="() => (revelados = [...revelados, extra.key])"
            />
          </DropdownSection>
        </DropdownBody>
      </DropdownContainer>

      <button
        v-if="temFiltro"
        type="button"
        class="h-8 px-2 text-sm font-medium rounded-lg text-n-brand hover:bg-n-alpha-1"
        @click="emit('reset')"
      >
        {{ t('FILTER_BAR.CLEAR') }}
      </button>
    </div>

    <div v-if="$slots.actions" class="flex items-center gap-2 ms-auto">
      <slot name="actions" />
    </div>
  </div>
</template>
